<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <main class="main-content">
      <!-- Page Hero -->
      <section class="page-hero">
        <div class="container">
          <div class="hero-content">
            <nav class="breadcrumb-nav">
              <a href="/" class="breadcrumb-link">Home</a>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-current">How It Works</span>
            </nav>
            <h1 class="page-title">How It Works</h1>
            <p class="page-subtitle">Your complete guide to using our county revenue services platform</p>
          </div>
        </div>
      </section>

      <!-- Quick Navigation -->
      <section class="quick-nav-section">
        <div class="container">
          <div class="quick-nav-grid">
            <a href="#overview" class="quick-nav-item">
              <div class="nav-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
              </div>
              <span>Overview</span>
            </a>
            <a href="#services" class="quick-nav-item">
              <div class="nav-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="3" width="7" height="7"></rect>
                  <rect x="14" y="3" width="7" height="7"></rect>
                  <rect x="14" y="14" width="7" height="7"></rect>
                  <rect x="3" y="14" width="7" height="7"></rect>
                </svg>
              </div>
              <span>Services</span>
            </a>
            <a href="#payment" class="quick-nav-item">
              <div class="nav-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                  <line x1="1" y1="10" x2="23" y2="10"></line>
                </svg>
              </div>
              <span>Payment</span>
            </a>
            <a href="#faq" class="quick-nav-item">
              <div class="nav-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
              </div>
              <span>FAQs</span>
            </a>
          </div>
        </div>
      </section>

      <!-- Overview Section -->
      <section id="overview" class="content-section">
        <div class="container">
          <div class="section-header">
            <span class="section-badge">Platform Overview</span>
            <h2 class="section-title">Welcome to {{ county?.countyName || 'County' }} Revenue Services</h2>
            <p class="section-subtitle">
              Our online platform allows you to access county services, pay bills, and manage your accounts from anywhere, at any time.
            </p>
          </div>

          <div class="overview-grid">
            <div class="overview-card main-card">
              <div class="card-content">
                <h3>What You Can Do</h3>
                <p>Our platform provides a convenient way to interact with county services without visiting our offices.</p>
                <ul class="feature-list">
                  <li>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                    Register new businesses and renew permits
                  </li>
                  <li>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                    Pay land rates and property taxes
                  </li>
                  <li>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                    Pay for daily and seasonal parking
                  </li>
                  <li>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                    Pay house rent and view statements
                  </li>
                  <li>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                    Look up and pay county bills
                  </li>
                  <li>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                    Print receipts and certificates
                  </li>
                </ul>
              </div>
              <div class="card-image">
                <div class="image-placeholder">
                  <svg width="120" height="120" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                    <line x1="8" y1="21" x2="16" y2="21"></line>
                    <line x1="12" y1="17" x2="12" y2="21"></line>
                  </svg>
                  <span>24/7 Online Access</span>
                </div>
              </div>
            </div>

            <div class="overview-stats">
              <div class="stat-card">
                <div class="stat-icon blue">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </div>
                <div class="stat-content">
                  <span class="stat-number">50,000+</span>
                  <span class="stat-label">Active Users</span>
                </div>
              </div>

              <div class="stat-card">
                <div class="stat-icon green">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                </div>
                <div class="stat-content">
                  <span class="stat-number">1M+</span>
                  <span class="stat-label">Transactions</span>
                </div>
              </div>

              <div class="stat-card">
                <div class="stat-icon purple">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                </div>
                <div class="stat-content">
                  <span class="stat-number">24/7</span>
                  <span class="stat-label">Availability</span>
                </div>
              </div>

              <div class="stat-card">
                <div class="stat-icon orange">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                  </svg>
                </div>
                <div class="stat-content">
                  <span class="stat-number">100%</span>
                  <span class="stat-label">Secure</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Getting Started Section -->
      <section class="steps-section">
        <div class="container">
          <div class="section-header centered">
            <span class="section-badge">Getting Started</span>
            <h2 class="section-title">How to Use Our Platform</h2>
            <p class="section-subtitle">Follow these simple steps to complete your transactions</p>
          </div>

          <div class="steps-timeline">
            <div class="step-card">
              <div class="step-number">1</div>
              <div class="step-content">
                <div class="step-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <rect x="3" y="3" width="7" height="7"></rect>
                    <rect x="14" y="3" width="7" height="7"></rect>
                    <rect x="14" y="14" width="7" height="7"></rect>
                    <rect x="3" y="14" width="7" height="7"></rect>
                  </svg>
                </div>
                <h3 style="color: white">Choose a Service</h3>
                <p style="color: white">Browse our available services and select the one you need. Each service has a dedicated page with clear instructions.</p>
                <ul class="step-details">
                  <li>Business Permits</li>
                  <li>Land Rates</li>
                  <li>Parking Fees</li>
                  <li>House Rent</li>
                  <li>Bill Payment</li>
                </ul>
              </div>
            </div>

            <div class="step-card">
              <div class="step-number">2</div>
              <div class="step-content">
                <div class="step-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </div>
                <h3 style="color: white">Fill in Details</h3>
                <p style="color: white">Enter the required information for your transaction. Make sure to double-check all details before proceeding.</p>
                <ul class="step-details">
                  <li>Personal information</li>
                  <li>Property/Business details</li>
                  <li>Contact information</li>
                  <li>Payment amount</li>
                </ul>
              </div>
            </div>

            <div class="step-card">
              <div class="step-number">3</div>
              <div class="step-content">
                <div class="step-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                    <line x1="1" y1="10" x2="23" y2="10"></line>
                  </svg>
                </div>
                <h3 style="color: white">Make Payment</h3>
                <p style="color: white">Pay securely using M-Pesa. You'll receive an STK push on your phone to complete the payment.</p>
                <ul class="step-details">
                  <li>Enter M-Pesa phone number</li>
                  <li>Receive STK push</li>
                  <li>Enter your M-Pesa PIN</li>
                  <li>Confirm payment</li>
                </ul>
              </div>
            </div>

            <div class="step-card">
              <div class="step-number">4</div>
              <div class="step-content">
                <div class="step-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <polyline points="6 9 6 2 18 2 18 9"></polyline>
                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                    <rect x="6" y="14" width="12" height="8"></rect>
                  </svg>
                </div>
                <h3 style="color: white">Get Your Receipt</h3>
                <p style="color: white">Once payment is confirmed, you can download or print your receipt. Keep it for your records.</p>
                <ul class="step-details">
                  <li>Instant confirmation</li>
                  <li>Digital receipt</li>
                  <li>Print or download</li>
                  <li>SMS confirmation</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Services Section -->
      <section id="services" class="services-section">
        <div class="container">
          <div class="section-header centered">
            <span class="section-badge">Our Services</span>
            <h2 class="section-title">Available Services</h2>
            <p class="section-subtitle">Explore the various county services you can access online</p>
          </div>

          <div class="services-grid">
            <!-- Business Permits -->
            <div class="service-card">
              <div class="service-icon blue">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                </svg>
              </div>
              <h3>Business Permits</h3>
              <p>Register new businesses, renew permits, and manage your business compliance with the county.</p>
              <div class="service-features">
                <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg> New Registration</span>
                <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg> Permit Renewal</span>
                <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg> Print Certificate</span>
              </div>
              <router-link to="/create-business" class="service-link">
                Get Started
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </router-link>
            </div>

            <!-- Land Rates -->
            <div class="service-card">
              <div class="service-icon green">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                  <polyline points="9 22 9 12 15 12 15 22"></polyline>
                </svg>
              </div>
              <h3>Land Rates</h3>
              <p>View your land rates balance, make payments, and access your property tax statements.</p>
              <div class="service-features">
                <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg> Check Balance</span>
                <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg> Make Payment</span>
                <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg> View Statement</span>
              </div>
              <router-link to="/land-rates" class="service-link">
                Get Started
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </router-link>
            </div>

            <!-- Daily Parking -->
            <div class="service-card">
              <div class="service-icon purple">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <rect x="1" y="3" width="15" height="13" rx="2"></rect>
                  <circle cx="5.5" cy="18.5" r="2.5"></circle>
                  <circle cx="12.5" cy="18.5" r="2.5"></circle>
                  <path d="M16 8h4l3 5v5h-2"></path>
                  <circle cx="19.5" cy="18.5" r="2.5"></circle>
                </svg>
              </div>
              <h3>Daily Parking</h3>
              <p>Pay for street and off-street parking quickly. Select your zone, vehicle type, and duration.</p>
              <div class="service-features">
                <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg> Daily Parking</span>
                <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg> Seasonal Pass</span>
                <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg> Instant Receipt</span>
              </div>
              <router-link to="/daily-parking" class="service-link">
                Get Started
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </router-link>
            </div>

            <!-- House Rent -->
            <div class="service-card">
              <div class="service-icon orange">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                  <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                  <line x1="12" y1="22.08" x2="12" y2="12"></line>
                </svg>
              </div>
              <h3>House Rent</h3>
              <p>Pay your county house rent, view your balance, and access rent statements online.</p>
              <div class="service-features">
                <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg> Pay Rent</span>
                <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg> View Arrears</span>
                <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg> Payment History</span>
              </div>
              <router-link to="/house-rents" class="service-link">
                Get Started
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </router-link>
            </div>

            <!-- Bill Payment -->
            <div class="service-card">
              <div class="service-icon teal">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                </svg>
              </div>
              <h3>Bill Payment</h3>
              <p>Look up any county bill using your bill number and make instant payments online.</p>
              <div class="service-features">
                <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg> Bill Lookup</span>
                <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg> Pay Any Bill</span>
                <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg> Multiple Payments</span>
              </div>
              <router-link to="/pay-bill" class="service-link">
                Get Started
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </router-link>
            </div>

            <!-- Property Services -->
            <div class="service-card">
              <div class="service-icon pink">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                  <path d="M9 12l2 2 4-4"></path>
                </svg>
              </div>
              <h3>Property Services</h3>
              <p>Update your property information in the valuation roll and manage property records.</p>
              <div class="service-features">
                <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg> Update Details</span>
                <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg> Transfer Ownership</span>
                <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg> Valuation Roll</span>
              </div>
              <router-link to="/property-self-service" class="service-link">
                Get Started
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </router-link>
            </div>
          </div>
        </div>
      </section>

      <!-- Payment Section -->
      <section id="payment" class="payment-section">
        <div class="container">
          <div class="payment-grid">
            <div class="payment-info">
              <span class="section-badge">Payment Methods</span>
              <h2 class="section-title">Secure M-Pesa Payments</h2>
              <p class="section-description">
                All payments on our platform are processed securely through M-Pesa. Here's how the payment process works:
              </p>

              <div class="payment-steps">
                <div class="payment-step">
                  <div class="payment-step-num">1</div>
                  <div class="payment-step-content">
                    <h4>Enter Phone Number</h4>
                    <p>Enter your M-Pesa registered phone number when prompted for payment.</p>
                  </div>
                </div>

                <div class="payment-step">
                  <div class="payment-step-num">2</div>
                  <div class="payment-step-content">
                    <h4>Receive STK Push</h4>
                    <p>You'll receive a pop-up notification on your phone from M-Pesa (Lipa Na M-Pesa).</p>
                  </div>
                </div>

                <div class="payment-step">
                  <div class="payment-step-num">3</div>
                  <div class="payment-step-content">
                    <h4>Enter M-Pesa PIN</h4>
                    <p>Enter your 4-digit M-Pesa PIN to authorize the payment. Do not share your PIN.</p>
                  </div>
                </div>

                <div class="payment-step">
                  <div class="payment-step-num">4</div>
                  <div class="payment-step-content">
                    <h4>Confirmation</h4>
                    <p>You'll receive an M-Pesa confirmation SMS and your receipt will be ready to print.</p>
                  </div>
                </div>
              </div>

              <div class="payment-note">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
                <div>
                  <strong>Important:</strong> Never share your M-Pesa PIN with anyone. Our system will never ask for your PIN directly.
                </div>
              </div>
            </div>

            <div class="payment-visual">
              <div class="phone-mockup">
                <div class="phone-screen">
                  <div class="mpesa-prompt">
                    <div class="prompt-header">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                        <line x1="12" y1="18" x2="12.01" y2="18"></line>
                      </svg>
                      <span>M-PESA</span>
                    </div>
                    <div class="prompt-body">
                      <p class="prompt-title">Lipa Na M-Pesa</p>
                      <div class="prompt-details">
                        <div class="prompt-row">
                          <span>Pay to:</span>
                          <strong>County Revenue</strong>
                        </div>
                        <div class="prompt-row">
                          <span>Amount:</span>
                          <strong>KES 1,500</strong>
                        </div>
                        <div class="prompt-row">
                          <span>Account:</span>
                          <strong>BLL-123456</strong>
                        </div>
                      </div>
                      <div class="prompt-pin">
                        <span>Enter M-Pesa PIN</span>
                        <div class="pin-dots">
                          <span></span><span></span><span></span><span></span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- FAQ Section -->
      <section id="faq" class="faq-section">
        <div class="container">
          <div class="section-header centered">
            <span class="section-badge">FAQs</span>
            <h2 class="section-title">Frequently Asked Questions</h2>
            <p class="section-subtitle">Find answers to common questions about our services</p>
          </div>

          <div class="faq-grid">
            <div class="faq-item" v-for="(faq, index) in faqs" :key="index">
              <button class="faq-question" @click="toggleFaq(index)" :class="{ active: activeFaq === index }">
                <span>{{ faq.question }}</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline :points="activeFaq === index ? '18 15 12 9 6 15' : '6 9 12 15 18 9'"></polyline>
                </svg>
              </button>
              <transition name="faq-expand">
                <div v-if="activeFaq === index" class="faq-answer">
                  <p>{{ faq.answer }}</p>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </section>

      <!-- CTA Section -->
      <section class="cta-section">
        <div class="container">
          <div class="cta-card">
            <div class="cta-content">
              <h2>Ready to Get Started?</h2>
              <p>Access county services from anywhere, anytime. It's fast, secure, and convenient.</p>
              <div class="cta-buttons">
                <router-link to="/" class="btn btn-primary btn-lg">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                    <polyline points="9 22 9 12 15 12 15 22"></polyline>
                  </svg>
                  Explore Services
                </router-link>
                <a href="#services" class="btn btn-outline btn-lg">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polygon points="10 8 16 12 10 16 10 8"></polygon>
                  </svg>
                  Learn More
                </a>
              </div>
            </div>
            <div class="cta-graphic">
              <div class="floating-icons">
                <div class="floating-icon icon-1">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                    <line x1="1" y1="10" x2="23" y2="10"></line>
                  </svg>
                </div>
                <div class="floating-icon icon-2">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                </div>
                <div class="floating-icon icon-3">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="6 9 6 2 18 2 18 9"></polyline>
                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                    <rect x="6" y="14" width="12" height="8"></rect>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Contact Section -->
      <section class="contact-section">
        <div class="container">
          <div class="contact-grid">
            <div class="contact-card">
              <div class="contact-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                </svg>
              </div>
              <h4>Phone Support</h4>
              <p>{{ county?.contact || '0700-000-000' }}</p>
              <span>Mon - Fri, 8am - 5pm</span>
            </div>

            <div class="contact-card">
              <div class="contact-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
              </div>
              <h4>Email Support</h4>
              <p>{{ county?.email || 'support@county.go.ke' }}</p>
              <span>We respond within 24 hours</span>
            </div>

            <div class="contact-card">
              <div class="contact-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
              </div>
              <h4>Visit Us</h4>
              <p>{{ county?.address || 'County Headquarters' }}</p>
              <span>Mon - Fri, 8am - 5pm</span>
            </div>

            <div class="contact-card">
              <div class="contact-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                  <line x1="12" y1="18" x2="12.01" y2="18"></line>
                </svg>
              </div>
              <h4>USSD Code</h4>
              <p>{{ county?.ussd || '*123#' }}</p>
              <span>Access services on any phone</span>
            </div>
          </div>
        </div>
      </section>
    </main>

    <FooterComponent />
  </div>
</template>

<script>
import { authUrl, execute } from "@/api";
import HeaderComponent from "@/components/HeaderComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";

export default {
  name: "HowItWorks",
  components: { HeaderComponent, FooterComponent },
  data() {
    return {
      county: null,
      activeFaq: null,
      faqs: [
        {
          question: "How do I register a new business?",
          answer: "To register a new business, go to the Business Permits section, click on 'New Registration', fill in your business details including location, type of business, and owner information, then proceed to make payment via M-Pesa. Once payment is confirmed, you can print your business permit certificate."
        },
        {
          question: "How do I check my land rates balance?",
          answer: "Navigate to the Land Rates section and enter your plot number or UPN (Unique Property Number). The system will display your current balance including any outstanding arrears. You can then choose to pay the full amount or a partial payment."
        },
        {
          question: "What payment methods are accepted?",
          answer: "Currently, we accept payments via M-Pesa (Lipa Na M-Pesa). When you initiate a payment, you'll receive an STK push to your phone. Simply enter your M-Pesa PIN to complete the transaction. We're working on adding more payment options in the future."
        },
        {
          question: "How do I print my receipt?",
          answer: "After a successful payment, you'll see a 'Print Receipt' button. Click on it to open your receipt in a new window, which you can then print or save as a PDF. You can also retrieve past receipts by going to the receipt lookup section and entering your transaction code."
        },
        {
          question: "What if my M-Pesa payment fails?",
          answer: "If your payment fails, first check that you have sufficient funds in your M-Pesa account and that you entered the correct PIN. If the issue persists, wait a few minutes and try again. If money was deducted but the transaction wasn't completed on our system, contact our support team with your M-Pesa transaction code."
        },
        {
          question: "How long does it take to process a permit?",
          answer: "For standard services like business permit renewals and parking payments, processing is instant upon successful payment. For new business registrations, processing typically takes 24-48 hours for verification. Property-related requests may take 3-5 business days."
        },
        {
          question: "Can I use the platform on my mobile phone?",
          answer: "Yes! Our platform is fully responsive and works on all devices including smartphones and tablets. Simply open your web browser and navigate to our website. You can also use the USSD code for basic services on any phone."
        },
        {
          question: "How do I update my property information?",
          answer: "Go to the Property Services section, search for your property using your plot number or owner details, verify your identity through OTP, then select the type of update you want to make. You'll need to upload supporting documents for verification."
        }
      ]
    };
  },

  mounted() {
    this.getCounty();
  },

  methods: {
    async getCounty() {
      try {
        const data = new FormData();
        data.append("function", "getCounty");
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.county = res.data.data.county;
        }
      } catch (error) {
        console.error('Error fetching county:', error);
      }
    },

    toggleFaq(index) {
      this.activeFaq = this.activeFaq === index ? null : index;
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.page-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
}

.main-content {
  flex: 1;
  padding-top: 72px;
}

/* Page Hero */
.page-hero {
  background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
  padding: 48px 0;
  color: white;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.breadcrumb-nav {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 14px;
}

.breadcrumb-link {
  color: rgba(255,255,255,0.7);
  text-decoration: none;
  transition: color 0.2s;
}

.breadcrumb-link:hover { color: white; }
.breadcrumb-separator { color: rgba(255,255,255,0.4); }
.breadcrumb-current { color: white; }

.page-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 36px;
  font-weight: 800;
  margin-bottom: 8px;
}

.page-subtitle {
  opacity: 0.9;
  font-size: 18px;
}

/* Quick Navigation */
.quick-nav-section {
  background: white;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 72px;
  z-index: 100;
}

.quick-nav-grid {
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: 16px 0;
}

.quick-nav-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  text-decoration: none;
  color: #64748b;
  border-radius: 10px;
  font-weight: 500;
  transition: all 0.2s;
}

.quick-nav-item:hover {
  background: #f1f5f9;
  color: #0f4c81;
}

.nav-icon {
  width: 40px;
  height: 40px;
  background: #f1f5f9;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.quick-nav-item:hover .nav-icon {
  background: #dbeafe;
  color: #0f4c81;
}

/* Section Styling */
.content-section {
  padding: 80px 0;
}

.section-header {
  margin-bottom: 48px;
}

.section-header.centered {
  text-align: center;
}

.section-badge {
  display: inline-block;
  padding: 6px 16px;
  background: #dbeafe;
  color: #1e40af;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 16px;
}

.section-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 32px;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 12px;
}

.section-subtitle {
  font-size: 16px;
  color: #64748b;
  max-width: 600px;
}

.section-header.centered .section-subtitle {
  margin: 0 auto;
}

/* Overview Section */
.overview-grid {
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 32px;
}

.overview-card.main-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
  display: grid;
  grid-template-columns: 1fr 1fr;
}

.card-content {
  padding: 40px;
}

.card-content h3 {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 16px;
}

.card-content > p {
  color: #64748b;
  margin-bottom: 24px;
  line-height: 1.6;
}

.feature-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.feature-list li {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  font-size: 15px;
  color: #475569;
}

.feature-list li svg {
  color: #059669;
  flex-shrink: 0;
}

.card-image {
  background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-placeholder {
  text-align: center;
  color: white;
}

.image-placeholder svg {
  opacity: 0.3;
  margin-bottom: 16px;
}

.image-placeholder span {
  display: block;
  font-size: 18px;
  font-weight: 600;
}

.overview-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.stat-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  gap: 16px;
}

.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-icon.blue { background: #dbeafe; color: #2563eb; }
.stat-icon.green { background: #d1fae5; color: #059669; }
.stat-icon.purple { background: #ede9fe; color: #7c3aed; }
.stat-icon.orange { background: #ffedd5; color: #ea580c; }

.stat-number {
  display: block;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 24px;
  font-weight: 800;
  color: #1e293b;
}

.stat-label {
  font-size: 14px;
  color: #64748b;
}

/* Steps Section */
.steps-section {
  background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
  padding: 80px 0;
  color: white;
}

.steps-section .section-badge {
  background: rgba(255,255,255,0.2);
  color: white;
}

.steps-section .section-title {
  color: white;
}

.steps-section .section-subtitle {
  color: rgba(255,255,255,0.8);
}

.steps-timeline {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-top: 48px;
}

.step-card {
  background: rgba(255,255,255,0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 32px 24px;
  position: relative;
}

.step-number {
  position: absolute;
  top: -16px;
  left: 24px;
  width: 40px;
  height: 40px;
  background: white;
  color: #0f4c81;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 18px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
}

.step-icon {
  width: 64px;
  height: 64px;
  background: rgba(255,255,255,0.1);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.step-content h3 {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 12px;
}

.step-content > p {
  font-size: 14px;
  opacity: 0.9;
  line-height: 1.6;
  margin-bottom: 16px;
}

.step-details {
  list-style: none;
  padding: 0;
  margin: 0;
}

.step-details li {
  font-size: 13px;
  opacity: 0.8;
  padding: 4px 0;
  padding-left: 16px;
  position: relative;
}

.step-details li::before {
  content: '•';
  position: absolute;
  left: 0;
}

/* Services Section */
.services-section {
  padding: 80px 0;
}

.services-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.service-card {
  background: white;
  border-radius: 20px;
  padding: 32px;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
  transition: all 0.3s;
}

.service-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px -8px rgba(0,0,0,0.15);
}

.service-icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.service-icon.blue { background: #dbeafe; color: #2563eb; }
.service-icon.green { background: #d1fae5; color: #059669; }
.service-icon.purple { background: #ede9fe; color: #7c3aed; }
.service-icon.orange { background: #ffedd5; color: #ea580c; }
.service-icon.teal { background: #ccfbf1; color: #0d9488; }
.service-icon.pink { background: #fce7f3; color: #db2777; }

.service-card h3 {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 12px;
}

.service-card > p {
  font-size: 14px;
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 20px;
}

.service-features {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
}

.service-features span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #059669;
  background: #d1fae5;
  padding: 6px 12px;
  border-radius: 20px;
}

.service-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #0f4c81;
  text-decoration: none;
  transition: gap 0.2s;
}

.service-link:hover {
  gap: 12px;
}

/* Payment Section */
.payment-section {
  background: #f1f5f9;
  padding: 80px 0;
}

.payment-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;
}

.section-description {
  font-size: 16px;
  color: #64748b;
  line-height: 1.7;
  margin-bottom: 32px;
}

.payment-steps {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.payment-step {
  display: flex;
  gap: 16px;
}

.payment-step-num {
  width: 36px;
  height: 36px;
  background: #0f4c81;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  flex-shrink: 0;
}

.payment-step-content h4 {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
}

.payment-step-content p {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.payment-note {
  display: flex;
  gap: 16px;
  padding: 20px;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 12px;
  margin-top: 32px;
}

.payment-note svg {
  color: #d97706;
  flex-shrink: 0;
}

.payment-note strong {
  color: #92400e;
}

.payment-note div {
  font-size: 14px;
  color: #78350f;
}

/* Phone Mockup */
.payment-visual {
  display: flex;
  justify-content: center;
}

.phone-mockup {
  width: 280px;
  background: #1e293b;
  border-radius: 36px;
  padding: 12px;
  box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);
}

.phone-screen {
  background: white;
  border-radius: 28px;
  padding: 20px;
  min-height: 480px;
}

.mpesa-prompt {
  height: 100%;
}

.prompt-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 24px;
}

.prompt-header svg {
  color: #059669;
}

.prompt-header span {
  font-weight: 700;
  color: #059669;
  font-size: 18px;
}

.prompt-title {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  text-align: center;
  margin-bottom: 24px;
}

.prompt-details {
  background: #f8fafc;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
}

.prompt-row {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #e2e8f0;
}

.prompt-row:last-child {
  border-bottom: none;
}

.prompt-row span {
  color: #64748b;
  font-size: 14px;
}

.prompt-row strong {
  color: #1e293b;
  font-size: 14px;
}

.prompt-pin {
  text-align: center;
}

.prompt-pin span {
  display: block;
  font-size: 14px;
  color: #64748b;
  margin-bottom: 16px;
}

.pin-dots {
  display: flex;
  justify-content: center;
  gap: 16px;
}

.pin-dots span {
  width: 16px;
  height: 16px;
  background: #0f4c81;
  border-radius: 50%;
}

/* FAQ Section */
.faq-section {
  padding: 80px 0;
}

.faq-grid {
  max-width: 800px;
  margin: 0 auto;
}

.faq-item {
  background: white;
  border-radius: 12px;
  margin-bottom: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  overflow: hidden;
}

.faq-question {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  transition: all 0.2s;
}

.faq-question:hover {
  background: #f8fafc;
}

.faq-question.active {
  background: #f8fafc;
}

.faq-question svg {
  color: #64748b;
  transition: transform 0.2s;
}

.faq-answer {
  padding: 0 24px 20px;
}

.faq-answer p {
  color: #64748b;
  line-height: 1.7;
  margin: 0;
}

.faq-expand-enter-active,
.faq-expand-leave-active {
  transition: all 0.3s ease;
}

.faq-expand-enter-from,
.faq-expand-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* CTA Section */
.cta-section {
  padding: 80px 0;
}

.cta-card {
  background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
  border-radius: 24px;
  padding: 64px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  align-items: center;
  overflow: hidden;
  position: relative;
}

.cta-content h2 {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 32px;
  font-weight: 800;
  color: white;
  margin-bottom: 16px;
}

.cta-content p {
  font-size: 16px;
  color: rgba(255,255,255,0.8);
  margin-bottom: 32px;
}

.cta-buttons {
  display: flex;
  gap: 16px;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 28px;
  border-radius: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  border: none;
}

.btn-lg {
  padding: 16px 32px;
  font-size: 16px;
}

.btn-primary {
  background: white;
  color: #0f4c81;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0,0,0,0.2);
}

.btn-outline {
  background: transparent;
  color: white;
  border: 2px solid rgba(255,255,255,0.3);
}

.btn-outline:hover {
  background: rgba(255,255,255,0.1);
  border-color: white;
}

.cta-graphic {
  position: relative;
  height: 200px;
}

.floating-icons {
  position: relative;
  width: 100%;
  height: 100%;
}

.floating-icon {
  position: absolute;
  background: rgba(255,255,255,0.1);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 20px;
  color: white;
  animation: float 6s ease-in-out infinite;
}

.floating-icon.icon-1 {
  top: 0;
  right: 20%;
  animation-delay: 0s;
}

.floating-icon.icon-2 {
  top: 50%;
  right: 40%;
  animation-delay: 2s;
}

.floating-icon.icon-3 {
  bottom: 0;
  right: 10%;
  animation-delay: 4s;
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-20px); }
}

/* Contact Section */
.contact-section {
  padding: 0 0 80px;
}

.contact-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}

.contact-card {
  background: white;
  border-radius: 16px;
  padding: 32px 24px;
  text-align: center;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
}

.contact-icon {
  width: 60px;
  height: 60px;
  background: #dbeafe;
  color: #2563eb;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
}

.contact-card h4 {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

.contact-card p {
  font-size: 15px;
  color: #0f4c81;
  font-weight: 600;
  margin-bottom: 4px;
}

.contact-card span {
  font-size: 13px;
  color: #64748b;
}

/* Responsive */
@media (max-width: 1024px) {
  .overview-grid {
    grid-template-columns: 1fr;
  }

  .overview-card.main-card {
    grid-template-columns: 1fr;
  }

  .card-image {
    min-height: 200px;
  }

  .overview-stats {
    grid-template-columns: repeat(4, 1fr);
  }

  .steps-timeline {
    grid-template-columns: repeat(2, 1fr);
  }

  .services-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .payment-grid {
    grid-template-columns: 1fr;
  }

  .payment-visual {
    order: -1;
  }

  .cta-card {
    grid-template-columns: 1fr;
    padding: 48px;
  }

  .cta-graphic {
    display: none;
  }

  .contact-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .page-title {
    font-size: 28px;
  }

  .quick-nav-grid {
    flex-wrap: wrap;
  }

  .quick-nav-item {
    padding: 10px 16px;
  }

  .overview-stats {
    grid-template-columns: repeat(2, 1fr);
  }

  .steps-timeline {
    grid-template-columns: 1fr;
  }

  .services-grid {
    grid-template-columns: 1fr;
  }

  .section-title {
    font-size: 26px;
  }

  .cta-buttons {
    flex-direction: column;
  }

  .contact-grid {
    grid-template-columns: 1fr;
  }
}
</style>
